import axios from 'axios';
import { refreshAccessToken } from 'api/AuthApi';
import { installAxiosErrorTagging } from '@api/utils/axiosErrorTagging';
import RedirectService from './RedirectService';
import Routes from './Routes';

// Web Locks name used to serialize token refreshes across every same-origin tab.
const REFRESH_LOCK = 'arkestro-token-refresh';

class AxiosService {
  // Deduplicates concurrent refreshes so a single-use refresh token isn't consumed twice.
  // Without this, when a tab wakes from being backgrounded past the access token TTL, every
  // in-flight request (healthCheck, TanStack refetches, etc.) hits 401 near-simultaneously and
  // each independently calls /refresh — the first succeeds and rotates the token, the rest come
  // back 401 (token already consumed) and their failure branch hard-reloads the page to /login
  // even though the session was successfully re-established. All concurrent 401s share this one
  // promise and get the same outcome.
  //
  // This promise is per-tab only. Multiple tabs share one refresh_token cookie but not this
  // static, so without cross-tab coordination they would each fire /refresh at once when the
  // access token expires — the first rotates the single-use token and the rest replay the
  // consumed one, which the API treats as compromise and signs every tab out. refreshOnce wraps
  // the network call in a Web Lock so refreshes serialize across tabs; because the winning tab's
  // Set-Cookie updates the shared cookie before the next tab runs, each refresh uses the latest
  // token instead of racing on the same one.
  static refreshPromise = null;

  // Serializes the actual /refresh network call across all same-origin tabs. Falls back to a
  // direct call where the Web Locks API is unavailable (older browsers, non-secure contexts).
  static refreshOnce = () => {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return navigator.locks.request(REFRESH_LOCK, () => refreshAccessToken());
    }

    return refreshAccessToken();
  };

  static setup = () => {
    axios.defaults.baseURL = Routes.BASE_PLATFORM_URL;
    axios.defaults.withCredentials = true;

    // Attempt to refresh the users access token for authorization
    axios.interceptors.response.use(
      response => response,
      async error => AxiosService.handleResponseError(error)
    );

    // Tag axios errors with endpoint/method/status so Datadog Error Tracking groups them
    // per (endpoint, status) instead of one giant "AxiosError" bucket. See PEPITA-828.
    installAxiosErrorTagging();
  };

  static handleResponseError = async error => {
    const originalRequest = error.config;
    const { url: originalPath } = originalRequest;

    let shouldAttemptRetry = RedirectService.wasUnauthorizedError(error);
    const hasRetried = !!originalRequest._retry;
    const isRefreshPath = Routes.REFRESH_IGNORE_PATHS.includes(originalPath);

    // If the original request was trying to sign in when it errored, or was a refresh attempt,
    // don't try and do anything with it so we can either surface errors to the UI or avoid
    // infinite loop on /refresh endpoint.
    if (isRefreshPath) shouldAttemptRetry = false;

    // ARKESTRO_CLIENT_AUDIT TODO Handle redirect when account is locked without infinite loop.
    // if (RedirectService.wasLockedError(error)) {
    //   RedirectService.redirectToLogin();
    //   return;
    // }

    if (shouldAttemptRetry && !hasRetried) {
      originalRequest._retry = true;

      if (!AxiosService.refreshPromise) {
        AxiosService.refreshPromise = AxiosService.refreshOnce()
          .finally(() => { AxiosService.refreshPromise = null; });
      }

      const refreshMyToken = await AxiosService.refreshPromise;

      // If token error when refreshing, this user needs to be redirected to the login page.
      if (refreshMyToken.data) {
        const {
          jwt: accessToken,
          refresh_token: refreshToken
        } = refreshMyToken.data;

        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        originalRequest.headers['jwt'] = accessToken;
        originalRequest.headers['refresh_token'] = refreshToken;

        return axios(originalRequest);
      }

      if (!RedirectService.isAuthenticationPage() && !RedirectService.isPublicPage()) {
        RedirectService.redirectToLogin();
      }
    }

    // Should always be returning some form of a promise at the end of this otherwise response could be
    // undefined in an async call.
    return Promise.reject(error);
  };
}

export default AxiosService;
